import { useEffect, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { TrendingUp, Zap, Clock } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const LIFT = 1.42; // conservative SalesCan conversion lift

const stats = [
    { icon: TrendingUp, value: "4.8x", label: "Avg. conversion lift" },
    { icon: Zap, value: "24/7", label: "AI lead qualification" },
    { icon: Clock, value: "15h", label: "Saved per rep / week" },
];

function Money({ value, className, testid }) {
    const spring = useSpring(0, { stiffness: 90, damping: 22 });
    useEffect(() => {
        spring.set(value);
    }, [value, spring]);
    const display = useTransform(spring, (v) => "$" + Math.round(v).toLocaleString("en-US"));
    return (
        <motion.span data-testid={testid} className={className}>
            {display}
        </motion.span>
    );
}

function Slider({ label, value, set, min, max, step, format, testid }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <label htmlFor={testid} className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    {label}
                </label>
                <span className="font-mono-tech text-sm font-semibold text-white">{format(value)}</span>
            </div>
            <input
                id={testid}
                data-testid={testid}
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => set(Number(e.target.value))}
                className="w-full accent-[#FF3B30] cursor-pointer h-1.5"
            />
        </div>
    );
}

export default function ROICalculator() {
    const [tab, setTab] = useState("calculator");
    const [leads, setLeads] = useState(80);
    const [dealSize, setDealSize] = useState(5000);
    const [closeRate, setCloseRate] = useState(18);

    const current = leads * dealSize * (closeRate / 100);
    const projected = current * LIFT;
    const extraAnnual = (projected - current) * 12;

    const tabBtn = (id, label, testid) => (
        <button
            data-testid={testid}
            onClick={() => setTab(id)}
            className={`px-3.5 py-1.5 rounded-full font-mono-tech text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === id ? "bg-[#FF3B30] text-white" : "text-slate-400 hover:text-white"
            }`}
        >
            {label}
        </button>
    );

    return (
        <div
            data-testid="hero-roi-calculator"
            className="rounded-3xl border border-white/10 bg-[#0E121B]/75 backdrop-blur-2xl p-7 sm:p-8 shadow-2xl"
        >
            <div className="flex items-center gap-1.5 mb-7 border border-white/10 rounded-full p-1 w-max">
                {tabBtn("calculator", "ROI Calculator", "roi-tab-calculator")}
                {tabBtn("impact", "Impact Index", "roi-tab-impact")}
            </div>

            {tab === "calculator" ? (
                <motion.div
                    key="calc"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="space-y-5"
                >
                    <Slider label="Monthly qualified leads" value={leads} set={setLeads} min={10} max={500} step={5} format={(v) => v} testid="roi-leads-slider" />
                    <Slider label="Average deal size" value={dealSize} set={setDealSize} min={1000} max={50000} step={500} format={(v) => "$" + v.toLocaleString("en-US")} testid="roi-deal-size-slider" />
                    <Slider label="Close rate" value={closeRate} set={setCloseRate} min={5} max={40} step={1} format={(v) => v + "%"} testid="roi-close-rate-slider" />

                    <div className="border-t border-white/10 pt-5 space-y-3">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">Current monthly revenue</span>
                            <Money value={current} testid="roi-current-revenue" className="font-mono-tech font-semibold text-slate-200" />
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">
                                With SalesCan <span className="text-[#FF3B30]">+42%</span>
                            </span>
                            <Money value={projected} testid="roi-projected-revenue" className="font-mono-tech font-semibold text-white" />
                        </div>
                    </div>

                    <div className="rounded-2xl border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-5 text-center">
                        <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">
                            Extra annual revenue
                        </p>
                        <Money value={extraAnnual} testid="roi-extra-annual" className="font-display font-black text-3xl text-[#FF3B30]" />
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                        Conservative estimate based on a 42% conversion lift. Most clients see more — your free audit includes a precise projection.
                    </p>
                </motion.div>
            ) : (
                <motion.div
                    key="impact"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="space-y-6"
                >
                    {stats.map((s) => (
                        <div key={s.label} className="flex items-center gap-4 border-b border-white/5 last:border-0 pb-6 last:pb-0">
                            <div className="w-11 h-11 rounded-2xl bg-[#FF3B30]/12 border border-[#FF3B30]/25 flex items-center justify-center shrink-0">
                                <s.icon className="w-5 h-5 text-[#FF3B30]" />
                            </div>
                            <div>
                                <p className="font-display font-bold text-2xl text-white">{s.value}</p>
                                <p className="text-sm text-slate-400">{s.label}</p>
                            </div>
                        </div>
                    ))}
                </motion.div>
            )}
        </div>
    );
}
